import React, {useEffect, useState} from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
import i18n from "i18next";
// Project imports
import API, {createApiConfig} from "../../../utils/API";
import {AxiosError} from "axios";
import Loading from "../../common/Loading";
import {ServerCommunicationAlert} from "../../common/errors";
import {
    ContactPersonCreateDTO,
    ContactPersonDTO,
    municipalityOrganizationType,
    MunicipalityType,
    municipalityTypes,
    nonMunicipalityOrganizationTypes,
    OrganizationProfileDTO,
    OrganizationType,
    organizationTypes,
    SimpleMunicipalityDTO,
} from "../../../models/organization";
import SccLogo from "../../../graphics/logo/SCC_favicon_COL.png";
// Material-UI imports
import {
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
} from "@material-ui/core";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
// Material-UI icons imports
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/PeopleAlt";
import KeyboardIcon from "@material-ui/icons/Keyboard";
import TimelineIcon from "@material-ui/icons/Timeline";
import DashboardIcon from "@material-ui/icons/Dashboard";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import BusinessIcon from "@material-ui/icons/Business";
import CancelIcon from "@material-ui/icons/Cancel";
import LaunchIcon from "@material-ui/icons/Launch";

// Graphics
import MunicipalitySelect from "./MunicipalitySelect";
import {ROUTES} from "../../../routes/routes";
import {Link as ReactLink} from "react-router-dom";
import {OrganizationPermissionsDTO} from "../../../models/members";
import {UploadableImage} from "../../common/images";
import {ImageDTO} from "../../../models/images";
import {isAdmin, isUser} from "../../../utils/auth";
import ContactPersonFormPart from "./ContactPersonFormPart";
import MapyCzMap from "./MapyCzMap/MapyCzMap";
import FullMembershipRequestControl from "../members/Members/FullMembershipRequestControl";
import AnonymousFullMembershipRequestControl from "../members/Members/AnonymousFullMembershipRequestControl";
import {ManualSidebar} from "../../../manual/components/ManualSidebar";
import {csPublicProfileSection} from "../../../manual/content/cs/CsPublicManualChapter";
import {csMunicipalityProfileSection} from "../../../manual/content/cs/CsMunicipalityManualChapter";
import {csOrganizationProfileSection} from "../../../manual/content/cs/CsOrganizationManualChapter";

export interface OrganizationProfileInfoProps {
    organizationProfile: OrganizationProfileDTO;
    editFirst?: boolean;
    organizationPermissions: OrganizationPermissionsDTO;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        pageHeader: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: theme.spacing(3),
        },
        backLink: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#374151",
            textDecoration: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
                color: "#2563EB",
            },
        },
        editButton: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
        },
        card: {
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
        },
        orgName: {
            fontSize: "1.75rem",
            fontWeight: 700,
            color: "#111827",
            margin: 0,
            marginBottom: theme.spacing(1),
        },
        nameDivider: {
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(1),
            backgroundColor: "#E5E7EB",
        },
        infoGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px 32px",
            [theme.breakpoints.down("sm")]: {
                gridTemplateColumns: "1fr",
            },
        },
        infoItem: {
            display: "flex",
            flexDirection: "column",
            gap: "2px",
        },
        infoLabel: {
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
        },
        infoValue: {
            fontSize: "0.9375rem",
            color: "#111827",
            fontWeight: 400,
        },
        infoLink: {
            color: "#2563EB",
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        aboutSection: {
            marginTop: theme.spacing(2),
        },
        aboutLabel: {
            fontSize: "1rem",
            fontWeight: 600,
            color: "#111827",
            marginBottom: theme.spacing(1),
        },
        aboutText: {
            fontSize: "0.9375rem",
            color: "#374151",
            lineHeight: 1.6,
        },
        logoWrapper: {
            width: "150px",
            height: "150px",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
            marginBottom: theme.spacing(1.5),
            backgroundColor: "#F3F4F6",
        },
        logo: {
            width: "100%",
            height: "100%",
            objectFit: "contain",
        },
        photoWrapper: {
            width: "100%",
            maxWidth: "300px",
            height: "180px",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
            marginBottom: theme.spacing(1.5),
            backgroundColor: "#EFF6FF",
        },
        photo: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        tabsRow: {
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginTop: theme.spacing(2),
        },
        tabButton: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            color: "#374151",
            borderColor: "#D1D5DB",
            fontSize: "0.875rem",
            padding: "6px 16px",
            "&:hover": {
                backgroundColor: "#F9FAFB",
                borderColor: "#9CA3AF",
            },
        },
        formCard: {
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
        },
        formTitle: {
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "#111827",
            marginBottom: theme.spacing(2),
        },
        formActions: {
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: theme.spacing(3),
            paddingTop: theme.spacing(2),
            borderTop: "1px solid #E5E7EB",
        },
        spaceAfter: {
            marginBottom: theme.spacing(2),
        },
        organizationName: {
            margin: 0,
            marginBottom: theme.spacing(1),
        },
        contactPerson: {
            padding: "12px 16px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            marginTop: theme.spacing(1),
        },
        contactPersonName: {
            fontWeight: 600,
            color: "#111827",
        },
        contactPersonRole: {
            color: "#6B7280",
            fontSize: "0.875rem",
        },
        contactPersonContacts: {
            marginTop: theme.spacing(1),
            color: "#374151",
            fontSize: "0.875rem",
        },
        membershipButton: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
        },
    })
);

export type FormValidationType =
    | "min"
    | "more"
    | "max"
    | "less"
    | "required"
    | "maxLength"
    | "exactLength"
    | "requiredEither"
    | "format"
    | "mustContain";

export type OrganizationProfileFormAttribute =
    | "name"
    | "description"
    | "organizationType"
    | "web"
    | "stateID"
    | "territorialCode"
    | "population"
    | "populationYear"
    | "areaKm2"
    | "level"
    | "municipalityType"
    | "contactPersonFullName"
    | "contactPersonEmail"
    | "contactPersonPhoneNumber"
    | "contactPersonRole"
    | "parentMunicipality";

interface FormError {
    validationType: FormValidationType;
    referencedValue?: string | null;
}

interface FormErrors extends Map<OrganizationProfileFormAttribute, FormError> {}

interface OrganizationProfileFormData {
    name: string;
    description: string | null;
    organizationType: OrganizationType;
    web: string | null;
    stateID: string | null;
    territorialCode: string;
    population: number | null;
    populationYear: number | null;
    areaKm2: string;
    level: number | null;
    municipalityType: MunicipalityType | null;
    contactPerson: ContactPersonDTO | ContactPersonCreateDTO | null;
    parentMunicipality: SimpleMunicipalityDTO | null;
}

const OrganizationProfile: React.FC<OrganizationProfileInfoProps> = (props: OrganizationProfileInfoProps) => {
    const classes = useStyles();
    const {keycloak, initialized} = useKeycloak();
    const [t] = useTranslation(["organization", "common"]);
    const [state, setState] = useState({
        name: (props.editFirst ? "edit" : "init") as "init" | "saving" | "saved" | "edit" | "failed",
        organizationProfile: props.organizationProfile,
    });

    const organizationInState = state.organizationProfile.organization;

    function formDataFromState(): OrganizationProfileFormData {
        return {
            name: organizationInState.name,
            description: organizationInState.description,
            organizationType: organizationInState.type,
            web: organizationInState.web,
            stateID: organizationInState.stateID,
            territorialCode: organizationInState.municipality?.territorialCode || "",
            population: organizationInState.municipality?.population || null,
            populationYear: organizationInState.municipality?.populationYear || null,
            areaKm2: organizationInState.municipality?.areaKm2?.toFixed(2)?.replace(".", ",") || "",
            level: organizationInState.municipality?.level || 1,
            municipalityType: organizationInState.municipality?.type || "TOWN",
            contactPerson: organizationInState.contactPerson as ContactPersonDTO | ContactPersonCreateDTO | null,
            parentMunicipality: organizationInState.municipality?.parentMunicipality || null,
        };
    }

    const [formData, setFormData] = useState(formDataFromState());
    const [formErrors, setFormErrors] = useState<FormErrors>(new Map());
    const formDataAreaNumeric = formData.areaKm2
        ? parseFloat(parseFloat(formData.areaKm2.replace(",", ".")).toFixed(2))
        : 0;
    const startEdit = (): void => {
        if (state.name === "saving") return;
        setState({...state, name: "edit"});
    };

    const validateFormData = () => {
        const newFormErrors = new Map(formErrors);

        if (!formData.name) newFormErrors.set("name", {validationType: "required"});
        else if (formData.name.length > 250) newFormErrors.set("name", {validationType: "max", referencedValue: "250"});

        if (formData.description && formData.description.length > 2000)
            newFormErrors.set("description", {validationType: "max", referencedValue: "2000"});

        if (!formData.web) newFormErrors.set("web", {validationType: "required"});
        else if (formData.web.length > 250) newFormErrors.set("web", {validationType: "max", referencedValue: "250"});

        if (!formData.organizationType) newFormErrors.set("organizationType", {validationType: "required"});

        if (formData.organizationType === municipalityOrganizationType) {
            const isKraj = formData.municipalityType === "KRAJ";
            const expectedCodeLength = isKraj ? 5 : 12;
            if (!formData.territorialCode) newFormErrors.set("territorialCode", {validationType: "required"});
            else if (formData.territorialCode.replace(" ", "").length !== expectedCodeLength)
                newFormErrors.set("territorialCode", {
                    validationType: "exactLength",
                    referencedValue: String(expectedCodeLength),
                });

            if (!formData.population) newFormErrors.set("population", {validationType: "required"});
            else if (formData.population <= 0)
                newFormErrors.set("population", {validationType: "more", referencedValue: "0"});
            else if (formData.population >= 100000000)
                newFormErrors.set("population", {validationType: "less", referencedValue: "100 000 000"});

            if (formData.populationYear) {
                if (formData.populationYear <= 2000)
                    newFormErrors.set("populationYear", {validationType: "more", referencedValue: "2000"});
                else if (formData.populationYear > new Date().getFullYear())
                    newFormErrors.set("populationYear", {
                        validationType: "less",
                        referencedValue: new Date().getFullYear().toString(),
                    });
            }

            if (formDataAreaNumeric < 0) newFormErrors.set("areaKm2", {validationType: "min", referencedValue: "0"});
            else if (formDataAreaNumeric > 10000)
                newFormErrors.set("areaKm2", {validationType: "max", referencedValue: "10 000"});

            if (!formData.stateID) newFormErrors.set("stateID", {validationType: "required"});
            else if (formData.stateID.trim().length !== 8)
                newFormErrors.set("stateID", {validationType: "exactLength", referencedValue: "8"});

            if (formData.contactPerson) {
                if (!formData.contactPerson.fullName)
                    newFormErrors.set("contactPersonFullName", {validationType: "required"});
                else if (formData.contactPerson.fullName.length > 250)
                    newFormErrors.set("contactPersonFullName", {validationType: "max", referencedValue: "250"});

                if (formData.contactPerson.email) {
                    if (formData.contactPerson.email.length > 250)
                        newFormErrors.set("contactPersonEmail", {validationType: "max", referencedValue: "250"});
                    if (!formData.contactPerson.email.includes("@", 1))
                        newFormErrors.set("contactPersonEmail", {validationType: "mustContain", referencedValue: "@"});
                }
                if (formData.contactPerson.phoneNumber) {
                    if (formData.contactPerson.phoneNumber.length > 100)
                        newFormErrors.set("contactPersonPhoneNumber", {validationType: "max", referencedValue: "100"});
                } else if (!formData.contactPerson.email) {
                    newFormErrors.set("contactPersonEmail", {
                        validationType: "requiredEither",
                        referencedValue: t("organization.phoneNumber"),
                    });
                    newFormErrors.set("contactPersonPhoneNumber", {
                        validationType: "requiredEither",
                        referencedValue: t("organization.email"),
                    });
                }

                if (formData.contactPerson.role && formData.contactPerson.role.length > 250)
                    newFormErrors.set("contactPersonRole", {validationType: "max", referencedValue: "250"});
            }
        }
        setFormErrors(newFormErrors);
        return newFormErrors.size == 0;
    };
    const clearFormError = (fieldName: OrganizationProfileFormAttribute) => {
        const newFormErrors = new Map(formErrors);
        newFormErrors.delete(fieldName);
        setFormErrors(newFormErrors);
    };
    const errorHelperText = (fieldName: OrganizationProfileFormAttribute) => {
        const error = formErrors.get(fieldName);
        if (!error) return null;
        return t("validation." + error.validationType, {referencedValue: error.referencedValue});
    };
    const setFormInputData = (fieldName: OrganizationProfileFormAttribute, data: OrganizationProfileFormData) => {
        clearFormError(fieldName);
        setFormData(data);
    };
    const saveEdit = (): void => {
        if (!validateFormData()) return;
        setState({...state, name: "saving"});
    };
    const cancelEdit = (): void => {
        setState({...state, name: "saved"});
        setFormData(formDataFromState());
    };
    const organizationTypesSelectOptions = (
        isAdmin(keycloak) ? organizationTypes : nonMunicipalityOrganizationTypes
    ).map((organizationType: string) => (
        <MenuItem key={organizationType} value={organizationType}>
            {t("type." + organizationType)}
        </MenuItem>
    ));
    const municipalityTypesSelectOptions = municipalityTypes.map((municipalityType: string) => (
        <MenuItem key={municipalityType} value={municipalityType}>
            {t("municipalityType." + municipalityType)}
        </MenuItem>
    ));

    useEffect(() => {
        const changed =
            formData.name !== organizationInState.name ||
            formData.description !== organizationInState.description ||
            formData.organizationType !== organizationInState.type ||
            formData.web !== organizationInState.web ||
            formData.stateID !== organizationInState.stateID ||
            formData.contactPerson !== organizationInState.contactPerson ||
            (formData.organizationType === municipalityOrganizationType &&
                (!organizationInState.municipality ||
                    formData.territorialCode !== organizationInState.municipality.territorialCode ||
                    formData.level !== organizationInState.municipality.level ||
                    formData.population !== organizationInState.municipality.population ||
                    formData.populationYear !== organizationInState.municipality.populationYear ||
                    formDataAreaNumeric !== organizationInState.municipality.areaKm2 ||
                    formData.municipalityType !== organizationInState.municipality.type ||
                    formData.parentMunicipality?.organizationId !==
                        organizationInState.municipality.parentMunicipality?.organizationId));
        if (state.name === "saving" && changed) {
            if (
                !isAdmin(keycloak) &&
                ((formData.organizationType === municipalityOrganizationType) !==
                    (organizationInState.type === municipalityOrganizationType) ||
                    formData.territorialCode !== organizationInState.municipality?.territorialCode ||
                    formData.level !== organizationInState.municipality?.level ||
                    formData.municipalityType !== organizationInState.municipality?.type ||
                    formData.parentMunicipality?.organizationId !==
                        organizationInState.municipality?.parentMunicipality?.organizationId)
            ) {
                setFormData({
                    ...formData,
                    organizationType: organizationInState.type,
                    territorialCode: organizationInState.municipality?.territorialCode || "",
                    level: organizationInState.municipality?.level || 1,
                    municipalityType: organizationInState.municipality?.type || "TOWN",
                    parentMunicipality: organizationInState.municipality?.parentMunicipality || null,
                });
                return setState({...state, name: "failed"});
            }
            const newMunicipality =
                formData.organizationType === municipalityOrganizationType
                    ? {
                          ...organizationInState.municipality,
                          territorialCode: formData.territorialCode,
                          level: formData.level,
                          type: formData.municipalityType,
                          population: formData.population,
                          populationYear: formData.populationYear,
                          areaKm2: formDataAreaNumeric,
                          parentMunicipality: formData.parentMunicipality,
                      }
                    : null;
            const organizationId = organizationInState.id;
            (organizationId ? API.put : API.post)(
                "/organizations/profile/" + (organizationId || ""),
                {
                    ...organizationInState,
                    ...formData,
                    type: formData.organizationType,
                    municipality: newMunicipality,
                },
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    setState({...state, name: "saved", organizationProfile: res.data});
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        } else if (state.name === "saving") {
            setState({...state, name: "saved"});
        }
    }, [state, organizationInState, formData, setState, keycloak, initialized]);

    const handleLogoUpload = (file: File) => {
        const fd = new FormData();
        fd.append("image", file);
        API.post<ImageDTO>(
            "/organizations/" + organizationInState.publicId + "/logo",
            fd,
            createApiConfig(keycloak, initialized)
        )
            .then((res) => {
                setState({
                    ...state,
                    name: "saved",
                    organizationProfile: {
                        ...state.organizationProfile,
                        organization: {...organizationInState, logo: res.data},
                    },
                });
            })
            .catch((err: AxiosError) => {
                setState({...state, name: "failed"});
            });
    };

    const handlePhotoUpload = (file: File) => {
        const fd = new FormData();
        fd.append("image", file);
        API.post<ImageDTO>(
            "/organizations/" + organizationInState.publicId + "/photo",
            fd,
            createApiConfig(keycloak, initialized)
        )
            .then((res) => {
                if (!organizationInState.municipality) return;
                setState({
                    ...state,
                    name: "saved",
                    organizationProfile: {
                        ...state.organizationProfile,
                        organization: {
                            ...organizationInState,
                            municipality: {
                                ...organizationInState.municipality,
                                photo: res.data,
                            },
                        },
                    },
                });
            })
            .catch((err: AxiosError) => {
                setState({...state, name: "failed"});
            });
    };

    if (state.name === "saving") {
        return (
            <Grid container item justifyContent="center">
                <Loading />
            </Grid>
        );
    } else if (state.name === "failed") {
        return (
            <Grid container item justifyContent="center">
                <ServerCommunicationAlert />
            </Grid>
        );
    }

    const canEditProfile =
        props.organizationPermissions.profilePermissionLevel === "READWRITE" ||
        props.organizationPermissions.profilePermissionLevel === "FULL";

    // ---- INFO VIEW ----
    const infoItems: Array<{label: string; value: React.ReactNode} | null> = [
        {
            label: t("organization.municipalityType"),
            value: organizationInState.municipality ? (
                organizationInState.municipality.level === 2 || organizationInState.municipality.level === 3 ? (
                    organizationInState.municipality.type === "TOWN" ? (
                        t("municipalityLevel." + organizationInState.municipality.level)
                    ) : (
                        <>
                            {t("municipalityType." + organizationInState.municipality.type)}
                            {" (" +
                                t("municipalityLevel." + organizationInState.municipality.level).toLowerCase() +
                                ")"}
                        </>
                    )
                ) : (
                    t("municipalityType." + organizationInState.municipality.type)
                )
            ) : (
                t("type." + organizationInState.type)
            ),
        },
        organizationInState.web
            ? {
                  label: t("organization.web"),
                  value: (
                      <Link
                          href={
                              !organizationInState.web.match(/^[a-zA-Z]+:\/\//)
                                  ? "//" + organizationInState.web
                                  : organizationInState.web
                          }
                          target="_blank"
                          rel="noopener"
                          className={classes.infoLink}
                      >
                          {organizationInState.web}
                          <LaunchIcon style={{fontSize: "0.85em", marginLeft: "4px", verticalAlign: "middle"}} />
                      </Link>
                  ),
              }
            : null,
        organizationInState.municipality &&
        organizationInState.municipality.type !== "KRAJ" &&
        i18n.exists("organization:county." + organizationInState.municipality.territorialCode.substring(0, 6))
            ? {
                  label: t("type.COUNTY"),
                  value: t("county." + organizationInState.municipality.territorialCode.substring(0, 6)),
              }
            : null,
        organizationInState.municipality &&
        i18n.exists(
            "organization:region." +
                (organizationInState.municipality.type === "KRAJ"
                    ? organizationInState.municipality.territorialCode
                    : organizationInState.municipality.territorialCode.substring(0, 5))
        )
            ? {
                  label: t("type.REGION"),
                  value: t(
                      "region." +
                          (organizationInState.municipality.type === "KRAJ"
                              ? organizationInState.municipality.territorialCode
                              : organizationInState.municipality.territorialCode.substring(0, 5))
                  ),
              }
            : null,
        organizationInState.municipality?.population
            ? {
                  label: t("organization.population"),
                  value:
                      organizationInState.municipality.population.toLocaleString("cs-CZ") +
                      (organizationInState.municipality.populationYear &&
                      organizationInState.municipality.populationYear != new Date().getFullYear()
                          ? " (" +
                            t("organization.inYear").toLowerCase() +
                            " " +
                            organizationInState.municipality.populationYear.toString() +
                            ")"
                          : ""),
              }
            : null,
        organizationInState.municipality?.areaKm2
            ? {
                  label: t("organization.area"),
                  value: (
                      <>
                          {organizationInState.municipality.areaKm2.toLocaleString("cs-CZ")} km
                          <sup>2</sup>
                      </>
                  ),
              }
            : null,
        isAdmin(keycloak) && organizationInState.municipality
            ? {
                  label:
                      organizationInState.municipality.type === "KRAJ"
                          ? t("organization.nuts3")
                          : t("organization.lau2"),
                  value: organizationInState.municipality.territorialCode,
              }
            : null,
        organizationInState.stateID
            ? {
                  label: t("organization.stateID"),
                  value: organizationInState.stateID,
              }
            : null,
    ];

    const organizationInfo = (
        <Box className={classes.card}>
            <h1 className={classes.orgName}>{organizationInState.name}</h1>
            <Divider className={classes.nameDivider} />
            <div className={classes.infoGrid}>
                {infoItems.filter(Boolean).map((item, idx) => (
                    <div key={idx} className={classes.infoItem}>
                        <span className={classes.infoLabel}>{item!.label}</span>
                        <span className={classes.infoValue}>{item!.value}</span>
                    </div>
                ))}
            </div>
            {organizationInState.description ? (
                <Box className={classes.aboutSection}>
                    <Divider style={{marginBottom: "12px", backgroundColor: "#E5E7EB"}} />
                    <div className={classes.aboutLabel}>About</div>
                    <div className={classes.aboutText}>{organizationInState.description}</div>
                </Box>
            ) : null}
            {organizationInState.contactPerson &&
            organizationInState.contactPerson.fullName &&
            (organizationInState.contactPerson.phoneNumber || organizationInState.contactPerson.email) ? (
                <Box style={{marginTop: "16px"}}>
                    <Divider style={{marginBottom: "12px", backgroundColor: "#E5E7EB"}} />
                    <div className={classes.aboutLabel}>{t("organization.contactPerson")}</div>
                    <Box className={classes.contactPerson}>
                        <div className={classes.contactPersonName}>{organizationInState.contactPerson.fullName}</div>
                        {organizationInState.contactPerson.role ? (
                            <div className={classes.contactPersonRole}>{organizationInState.contactPerson.role}</div>
                        ) : null}
                        <Box className={classes.contactPersonContacts}>
                            {organizationInState.contactPerson.phoneNumber ? (
                                <div>{organizationInState.contactPerson.phoneNumber}</div>
                            ) : null}
                            {organizationInState.contactPerson.email ? (
                                <div>{organizationInState.contactPerson.email}</div>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </Box>
    );

    // ---- EDIT FORM ----
    const organizationForm = (
        <Box className={classes.formCard}>
            <div className={classes.formTitle}>{t("common:actions.edit")}</div>
            <form autoComplete="off">
                {isAdmin(keycloak) || !organizationInState.municipality ? (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                id="name"
                                label={t("organization.name")}
                                value={formData.name || ""}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                className={classes.spaceAfter}
                                onChange={(e): void =>
                                    setFormInputData("name", {
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                error={formErrors.has("name")}
                                helperText={errorHelperText("name")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl
                                required
                                style={{width: "100%"}}
                                error={formErrors.has("organizationType")}
                                className={classes.spaceAfter}
                                variant="outlined"
                                size="small"
                            >
                                <InputLabel id="name-label">{t("organization.type")}</InputLabel>
                                <Select
                                    id="type"
                                    labelId="name-label"
                                    value={formData.organizationType}
                                    required
                                    fullWidth
                                    label={t("organization.type")}
                                    onChange={(e): void =>
                                        setFormInputData("organizationType", {
                                            ...formData,
                                            organizationType: e.target.value as OrganizationType,
                                        })
                                    }
                                >
                                    {organizationTypesSelectOptions}
                                </Select>
                                {formErrors.has("organizationType") ? (
                                    <FormHelperText>{errorHelperText("organizationType")}</FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>
                    </>
                ) : (
                    <h1 className={classes.organizationName}>{organizationInState.name}</h1>
                )}
                <Grid item xs={12}>
                    <TextField
                        id="description"
                        label={t("organization.description")}
                        value={formData.description || ""}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        size="small"
                        className={classes.spaceAfter}
                        onChange={(e): void =>
                            setFormInputData("description", {
                                ...formData,
                                description: e.target.value,
                            })
                        }
                        error={formErrors.has("description")}
                        helperText={errorHelperText("description")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="web"
                        label={t("organization.web")}
                        value={formData.web || ""}
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        className={classes.spaceAfter}
                        onChange={(e): void => setFormInputData("web", {...formData, web: e.target.value})}
                        error={formErrors.has("web")}
                        helperText={errorHelperText("web")}
                    />
                </Grid>
                {formData.organizationType === municipalityOrganizationType ? (
                    <div>
                        {isAdmin(keycloak) ? (
                            <Grid item xs={12}>
                                <FormControl
                                    required
                                    style={{width: "100%"}}
                                    error={formErrors.has("municipalityType")}
                                    className={classes.spaceAfter}
                                    variant="outlined"
                                    size="small"
                                >
                                    <InputLabel id="municipalityType-label">
                                        {t("organization.municipalityType")}
                                    </InputLabel>
                                    <Select
                                        id="municipalityType"
                                        labelId="municipalityType-label"
                                        value={formData.municipalityType}
                                        required
                                        fullWidth
                                        label={t("organization.municipalityType")}
                                        onChange={(e): void =>
                                            setFormInputData("municipalityType", {
                                                ...formData,
                                                municipalityType: e.target.value as MunicipalityType,
                                                level:
                                                    (e.target.value as MunicipalityType) === "CAPITAL_CITY"
                                                        ? 4
                                                        : formData.level,
                                            })
                                        }
                                    >
                                        {municipalityTypesSelectOptions}
                                    </Select>
                                    {formErrors.has("municipalityType") ? (
                                        <FormHelperText>{errorHelperText("municipalityType")}</FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Grid>
                        ) : null}
                        {isAdmin(keycloak) ? (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        id="territorialCode"
                                        label={
                                            formData.municipalityType === "KRAJ"
                                                ? t("organization.nuts3")
                                                : t("organization.lau2")
                                        }
                                        value={formData.territorialCode || ""}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        size="small"
                                        className={classes.spaceAfter}
                                        onChange={(e): void =>
                                            setFormInputData("territorialCode", {
                                                ...formData,
                                                territorialCode: e.target.value,
                                            })
                                        }
                                        error={formErrors.has("territorialCode")}
                                        helperText={errorHelperText("territorialCode")}
                                    />
                                </Grid>
                                <Grid container spacing={2} className={classes.spaceAfter}>
                                    <Grid item xs={formData.municipalityType === "KRAJ" ? 12 : 6}>
                                        <TextField
                                            id="region"
                                            label={t("type.REGION")}
                                            value={(() => {
                                                const isKraj = formData.municipalityType === "KRAJ";
                                                const code = formData.territorialCode;
                                                const regionKey = isKraj ? code : code?.substring(0, 5);
                                                const loadsFrom = isKraj
                                                    ? t("placeholders.loads_from_nuts3")
                                                    : t("placeholders.loads_from_lau2");
                                                if (!code?.length || code.length < 5)
                                                    return t("type.REGION") + " " + loadsFrom;
                                                return i18n.exists("organization:region." + regionKey)
                                                    ? t("region." + regionKey)
                                                    : t("placeholders.unknown");
                                            })()}
                                            disabled
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            inputProps={{readOnly: true}}
                                        />
                                    </Grid>
                                    {formData.municipalityType !== "KRAJ" ? (
                                        <Grid item xs={6}>
                                            <TextField
                                                id="county"
                                                label={t("type.COUNTY")}
                                                value={
                                                    formData.territorialCode?.length &&
                                                    formData.territorialCode?.length >= 6
                                                        ? i18n.exists(
                                                              "organization:county." +
                                                                  formData.territorialCode?.substring(0, 6)
                                                          )
                                                            ? t("county." + formData.territorialCode?.substring(0, 6))
                                                            : t("placeholders.unknown")
                                                        : t("type.COUNTY") + " " + t("placeholders.loads_from_lau2")
                                                }
                                                disabled
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                inputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </>
                        ) : null}
                        <Grid container spacing={2} className={classes.spaceAfter}>
                            <Grid item xs={6}>
                                <TextField
                                    id="population"
                                    label={t("organization.population")}
                                    value={formData.population || ""}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    size="small"
                                    onChange={(e): void => {
                                        const number = parseInt(e.target.value.replace(" ", ""));
                                        setFormInputData("population", {
                                            ...formData,
                                            population: number >= 0 ? number : null,
                                        });
                                    }}
                                    error={formErrors.has("population")}
                                    helperText={errorHelperText("population")}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="populationYear"
                                    label={t("organization.populationYear")}
                                    value={formData.populationYear || ""}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    onChange={(e): void => {
                                        const number = parseInt(e.target.value.replace(" ", ""));
                                        setFormInputData("populationYear", {
                                            ...formData,
                                            populationYear: number >= 0 ? number : null,
                                        });
                                    }}
                                    error={formErrors.has("populationYear")}
                                    helperText={errorHelperText("populationYear")}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="area"
                                label={
                                    <span>
                                        {t("organization.area") + " (km"}
                                        <sup>2</sup>)
                                    </span>
                                }
                                value={formData.areaKm2}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                className={classes.spaceAfter}
                                onChange={(e): void => {
                                    setFormInputData("areaKm2", {...formData, areaKm2: e.target.value});
                                }}
                                error={formErrors.has("areaKm2")}
                                helperText={errorHelperText("areaKm2")}
                            />
                        </Grid>
                        {isAdmin(keycloak) && (!formData.level || formData.level <= 3) ? (
                            <Grid item xs={12} className={classes.spaceAfter}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={!!formData.level && formData.level === 3}
                                            disabled={!!formData.level && formData.level >= 4}
                                            onChange={(e): void =>
                                                setFormInputData("level", {
                                                    ...formData,
                                                    level: e.target.checked ? 3 : 2,
                                                })
                                            }
                                            name="selected"
                                        />
                                    }
                                    label={t("municipalityLevel.3")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={!!formData.level && (formData.level === 2 || formData.level === 3)}
                                            disabled={!!formData.level && formData.level >= 3}
                                            onChange={(e): void =>
                                                setFormInputData("level", {
                                                    ...formData,
                                                    level: e.target.checked ? 2 : 1,
                                                })
                                            }
                                            name="selected"
                                        />
                                    }
                                    label={t("municipalityLevel.2")}
                                />
                            </Grid>
                        ) : null}
                        {isAdmin(keycloak) && (!formData.level || formData.level <= 2) ? (
                            <Grid item xs={12}>
                                <MunicipalitySelect
                                    label={t("organization.parentMunicipality")}
                                    value={formData.parentMunicipality}
                                    fullWidth
                                    required
                                    className={classes.spaceAfter}
                                    onChange={(e, value): void => {
                                        setFormInputData("parentMunicipality", {
                                            ...formData,
                                            parentMunicipality: value,
                                        });
                                    }}
                                    error={formErrors.has("parentMunicipality")}
                                    helperText={errorHelperText("parentMunicipality")}
                                />
                            </Grid>
                        ) : null}
                    </div>
                ) : null}

                <Grid item xs={12}>
                    <TextField
                        id="stateID"
                        label={t("organization.stateID")}
                        value={formData.stateID || ""}
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        className={classes.spaceAfter}
                        onChange={(e): void => setFormInputData("stateID", {...formData, stateID: e.target.value})}
                        error={formErrors.has("stateID")}
                        helperText={errorHelperText("stateID")}
                    />
                </Grid>
                <ContactPersonFormPart
                    contactPerson={formData.contactPerson}
                    onChange={(contactPerson) => setFormData({...formData, contactPerson: contactPerson})}
                    onFormDataChange={(fieldName) => clearFormError(fieldName)}
                    formErrors={formErrors}
                />

                <Box className={classes.formActions}>
                    <Button
                        variant="outlined"
                        onClick={cancelEdit}
                        startIcon={<CancelIcon />}
                        className={classes.editButton}
                    >
                        {t("common:actions.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={saveEdit}
                        startIcon={<SaveIcon />}
                        className={classes.editButton}
                    >
                        {t("common:actions.save")}
                    </Button>
                </Box>
            </form>
        </Box>
    );

    // ---- NAVIGATION TABS ----
    const navTabs: JSX.Element[] = [];
    if (state.name !== "edit" && organizationInState.publicId) {
        if (props.organizationPermissions.profilePermissionLevel === "FULL")
            navTabs.push(
                <Button
                    key="members"
                    component={ReactLink}
                    to={ROUTES.organizationMembers.create(organizationInState.publicId)}
                    variant="outlined"
                    size="small"
                    startIcon={<PeopleIcon />}
                    className={classes.tabButton}
                >
                    {t("organization.members")}
                </Button>
            );
        if (
            state.organizationProfile.organization.municipality &&
            (state.organizationProfile.publicTrackedIndicatorGroups.length > 0 ||
                props.organizationPermissions.trackedIndicatorsPermissionLevel === "READONLY" ||
                props.organizationPermissions.trackedIndicatorsPermissionLevel === "READWRITE")
        )
            navTabs.push(
                <Button
                    key="trackedIndicatorGroups"
                    component={ReactLink}
                    to={ROUTES.trackedIndicatorGroups.create(organizationInState.publicId)}
                    variant="outlined"
                    size="small"
                    startIcon={<TimelineIcon />}
                    className={classes.tabButton}
                >
                    {t("organization.trackedIndicatorGroups")}
                </Button>
            );
        navTabs.push(
            <Button
                key="dashboard"
                component={ReactLink}
                to={ROUTES.municipalityDashboard.create(organizationInState.publicId)}
                variant="outlined"
                size="small"
                startIcon={<DashboardIcon />}
                className={classes.tabButton}
            >
                {t("organization.dashboard")}
            </Button>
        );
        if (state.organizationProfile.canReadValues)
            navTabs.push(
                <Button
                    key="valuesInput"
                    component={ReactLink}
                    to={ROUTES.valuesInput.create(organizationInState.publicId)}
                    variant="outlined"
                    size="small"
                    startIcon={<KeyboardIcon />}
                    className={classes.tabButton}
                >
                    {t("ui.valuesInput")}
                </Button>
            );
        navTabs.push(
            <Button
                key="libraries"
                component={ReactLink}
                to={ROUTES.organizationLibraries.create(organizationInState.publicId)}
                variant="outlined"
                size="small"
                startIcon={<AccountTreeIcon />}
                className={classes.tabButton}
            >
                {t("ui.libraries")}
            </Button>
        );
        if ((canEditProfile || isAdmin(keycloak)) && organizationInState.municipality?.type === "KRAJ")
            navTabs.push(
                <Button
                    key="subOrganizations"
                    component={ReactLink}
                    to={ROUTES.subOrganizations.create(organizationInState.publicId)}
                    variant="outlined"
                    size="small"
                    startIcon={<BusinessIcon />}
                    className={classes.tabButton}
                >
                    {t("organization.subOrganizations")}
                </Button>
            );
    }

    // ---- MEMBERSHIP REQUEST ----
    const membershipControl = () => {
        const content = (
            <Button
                key="request-menu-item"
                variant="outlined"
                size="small"
                startIcon={<ContactMailIcon />}
                className={classes.membershipButton}
            >
                {t("ui.requestFullMembership")}
            </Button>
        );
        return isUser(keycloak) ? (
            <FullMembershipRequestControl key="request-control" organizationProfile={state.organizationProfile}>
                {content}
            </FullMembershipRequestControl>
        ) : (
            <AnonymousFullMembershipRequestControl
                key="anonymous-request-control"
                organizationProfile={state.organizationProfile}
            >
                {content}
            </AnonymousFullMembershipRequestControl>
        );
    };

    return (
        <>
            <ManualSidebar
                section={
                    canEditProfile
                        ? state.organizationProfile.organization.municipality
                            ? csMunicipalityProfileSection
                            : csOrganizationProfileSection
                        : csPublicProfileSection
                }
            />
            <Container maxWidth="lg">
                {/* Page header row */}
                <Box className={classes.pageHeader}>
                    <ReactLink to={ROUTES.municipalities.path} className={classes.backLink}>
                        <ArrowBackIcon style={{fontSize: "1rem"}} />
                        {t("common:actions.back")}
                    </ReactLink>
                    <Box style={{display: "flex", gap: "8px", alignItems: "center"}}>
                        {!canEditProfile && membershipControl()}
                        {canEditProfile && state.name !== "edit" && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<EditIcon />}
                                onClick={startEdit}
                                className={classes.editButton}
                            >
                                {t("common:actions.edit")}
                            </Button>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Left column: images */}
                    {organizationInState.id ? (
                        <Grid item xs={12} sm={4} md={3}>
                            {organizationInState.logo || canEditProfile ? (
                                <UploadableImage
                                    key={organizationInState.logo?.updatedAt ?? "empty"}
                                    src={
                                        organizationInState.logo
                                            ? API.defaults.baseURL +
                                              "/organizations/" +
                                              organizationInState.publicId +
                                              "/logo?timestamp=" +
                                              organizationInState.logo.updatedAt
                                            : null
                                    }
                                    alt={t("organization.logo")}
                                    wrapperClass={classes.logoWrapper}
                                    imageClass={classes.logo}
                                    editable={canEditProfile && state.name === "edit"}
                                    onConfirm={handleLogoUpload}
                                    emptyImage={SccLogo}
                                    editButtonText={
                                        t(organizationInState.logo ? "ui.edit" : "ui.upload") + t("ui._logo")
                                    }
                                />
                            ) : null}
                            {organizationInState.municipality &&
                            (organizationInState.municipality.photo || (canEditProfile && state.name === "edit")) ? (
                                <UploadableImage
                                    key={organizationInState.municipality?.photo?.updatedAt ?? "empty"}
                                    src={
                                        organizationInState.municipality?.photo
                                            ? API.defaults.baseURL +
                                              "/organizations/" +
                                              organizationInState.publicId +
                                              "/photo?timestamp=" +
                                              organizationInState.municipality.photo.updatedAt
                                            : null
                                    }
                                    alt={t("organization.photo")}
                                    wrapperClass={classes.photoWrapper}
                                    imageClass={classes.photo}
                                    editable={canEditProfile && state.name === "edit"}
                                    onConfirm={handlePhotoUpload}
                                    emptyImage={SccLogo}
                                    editButtonText={
                                        t(organizationInState.municipality.photo ? "ui.edit" : "ui.upload") +
                                        t("ui._photo")
                                    }
                                />
                            ) : null}
                        </Grid>
                    ) : null}

                    {/* Right column: info or form */}
                    <Grid item xs={12} sm={organizationInState.id ? 8 : 12} md={organizationInState.id ? 9 : 12}>
                        {state.name === "edit" ? organizationForm : organizationInfo}

                        {/* Navigation tabs at bottom */}
                        {navTabs.length > 0 && <Box className={classes.tabsRow}>{navTabs}</Box>}
                    </Grid>
                </Grid>

                {/* Map */}
                {state.name !== "edit" && organizationInState.municipality ? (
                    <Box
                        style={{
                            marginTop: "24px",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid #E5E7EB",
                        }}
                    >
                        <MapyCzMap
                            name={organizationInState.name}
                            county={
                                organizationInState.municipality.type === "KRAJ"
                                    ? t("region." + organizationInState.municipality.territorialCode)
                                    : t("county." + organizationInState.municipality.territorialCode.substring(0, 6))
                            }
                            wgs84e={organizationInState.municipality.wgs84e}
                            wgs84n={organizationInState.municipality.wgs84n}
                            height="400px"
                            style={{maxHeight: "50vh"}}
                            width="100%"
                            zoom={
                                organizationInState.municipality.type === "CAPITAL_CITY"
                                    ? 9
                                    : organizationInState.municipality.type === "STATUTE_CITY"
                                    ? 10
                                    : organizationInState.municipality.type === "CITY"
                                    ? 11
                                    : organizationInState.municipality.type === "MARKET_TOWN" ||
                                      (organizationInState.municipality.level &&
                                          organizationInState.municipality.level > 1)
                                    ? 12
                                    : 13
                            }
                        />
                    </Box>
                ) : null}
            </Container>
        </>
    );
};

export default OrganizationProfile;
